import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Quiz de 3 passos que cruza o que o visitante busca (motivação × disposição ×
 * tempo) com o perfil de cada passeio e recomenda 1 a 3 atividades.
 * Eixos definidos a partir das dúvidas reais das SERPs (radar de conteúdo):
 * o que quer sentir (aventura/natureza/sossego) × pique/leve/médio × quanto tempo tem.
 * Pontua os itens da taxonomia de passeios (náuticos/esportes/experiências). 100% client-side.
 */

type Item = {
  id: string;
  titulo: string;
  icon: string;
  blurb: string;
  difficulty: string | null;
  profile: string[];
  priceFrom: number | null;
  priceNote?: string | null;
  reservavel?: boolean;
  link: string | null;
  subtipoKey: string;
};
type Answers = { motivacao?: string; disposicao?: string; tempo?: string };

const STEPS = [
  {
    key: 'motivacao' as const,
    title: 'O que você quer sentir nesse passeio?',
    options: [
      { v: 'aventura', label: 'Adrenalina e aventura', icon: '🤙' },
      { v: 'natureza', label: 'Natureza e vida marinha', icon: '🐬' },
      { v: 'sossego', label: 'Sossego, contemplação e bem-estar', icon: '🌅' },
    ],
  },
  {
    key: 'disposicao' as const,
    title: 'Sua disposição e com quem vai?',
    options: [
      { v: 'pique', label: 'Pique total, encaro esforço e mar agitado', icon: '💪' },
      { v: 'medio', label: 'Nível médio — topo caminhada moderada', icon: '🚶' },
      { v: 'leve', label: 'Ritmo leve (crianças, idosos ou enjoo fácil)', icon: '🧒' },
    ],
  },
  {
    key: 'tempo' as const,
    title: 'Quanto tempo você tem pra esse passeio?',
    options: [
      { v: 'curto', label: 'Poucas horas / meio período', icon: '⏱️' },
      { v: 'dia', label: 'Um dia inteiro', icon: '🌞' },
      { v: 'varios', label: 'Vários dias — dá pra encaixar mais de um', icon: '📅' },
    ],
  },
];

// mapeia motivação → tags de perfil da taxonomia
const MOTIV_TAGS: Record<string, string[]> = {
  aventura: ['Aventura'],
  natureza: ['Natureza'],
  sossego: ['Sossego', 'Bem-estar', 'Romântico', 'Gastronomia'],
};
const DIFF_RANK: Record<string, number> = { Leve: 1, Moderada: 2, Puxada: 3 };

function score(item: Item, a: Answers): number {
  let s = 0;
  // 1) motivação (peso maior — define a "prateleira")
  const tags = MOTIV_TAGS[a.motivacao || ''] || [];
  if (item.profile.some(p => tags.includes(p))) s += 4;
  // 2) disposição vs dificuldade
  const d = DIFF_RANK[item.difficulty || 'Leve'] || 1;
  if (a.disposicao === 'leve') s += d === 1 ? 3 : d === 2 ? 0 : -3;
  if (a.disposicao === 'medio') s += d <= 2 ? 2 : 0;
  if (a.disposicao === 'pique') s += d >= 2 ? 2 : 1;
  // 3) tempo (nudge leve): passeios de barco "resumem tudo" pra quem tem pouco tempo
  if (a.tempo === 'curto' && (item.id === 'barco-volta-ilha' || /min|30/.test(item.priceNote || ''))) s += 1;
  return s;
}

function reasons(item: Item, a: Answers): string[] {
  const out: string[] = [];
  const tags = MOTIV_TAGS[a.motivacao || ''] || [];
  const hit = item.profile.find(p => tags.includes(p));
  if (hit) out.push(hit);
  if (a.disposicao === 'leve' && item.difficulty === 'Leve') out.push('Baixo esforço');
  if (a.disposicao === 'pique' && (item.difficulty === 'Moderada' || item.difficulty === 'Puxada')) out.push('Encara');
  if (item.priceFrom) out.push(`a partir de R$${item.priceFrom}`);
  else if (item.reservavel === false) out.push('Por conta própria');
  return [...new Set(out.filter(Boolean))].slice(0, 3);
}

export function OQueFazerQuiz({ taxonomia }: { taxonomia: any }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [done, setDone] = useState(false);

  const items: Item[] = (taxonomia?.subtipos || []).flatMap((s: any) =>
    (s.itens || []).map((it: any) => ({ ...it, subtipoKey: s.key }))
  );

  const pick = (key: string, v: string) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  let results: Item[] = [];
  if (done) {
    const ranked = items.map(it => ({ it, s: score(it, answers) })).sort((a, b) => b.s - a.s);
    const top = ranked[0]?.s ?? 0;
    const limit = answers.tempo === 'curto' ? 1 : answers.tempo === 'dia' ? 2 : 3;
    results = ranked.filter(r => r.s >= top - 1.5 && r.s > 0).slice(0, limit).map(r => r.it);
  }

  const hrefFor = (it: Item) => it.link || `#subtipo-${it.subtipoKey}`;

  return (
    <section className="mb-16 bg-teal-deep -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="quiz">
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-body text-sm text-sand-200 font-semibold uppercase tracking-wider">Teste rápido</span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-1 mb-2">Qual passeio é a sua cara?</h2>
        <p className="font-body text-sand-100/90 mb-8">Responda 3 perguntas e eu indico por onde começar.</p>

        {!done ? (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <div className="flex items-center gap-2 mb-5">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-coral' : 'bg-sand-200'}`} />
              ))}
            </div>
            <p className="font-body text-xs text-warm-gray mb-1">Passo {step + 1} de {STEPS.length}</p>
            <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-5">{STEPS[step].title}</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {STEPS[step].options.map(opt => (
                <button
                  key={opt.v}
                  onClick={() => pick(STEPS[step].key, opt.v)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-sand-200 hover:border-coral hover:bg-coral/5 transition-all text-left"
                >
                  <span className="text-2xl flex-shrink-0">{opt.icon}</span>
                  <span className="font-body font-medium text-warm-dark text-sm">{opt.label}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-5 font-body text-sm text-warm-gray hover:text-warm-dark">
                ← Voltar
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <p className="font-body text-sm text-coral font-semibold uppercase tracking-wider mb-1">
              {results.length > 1 ? 'Suas melhores opções' : 'O passeio com a sua cara'}
            </p>
            <h3 className="font-display text-2xl text-warm-dark mb-5">
              {results.length ? `Encontrei ${results.length === 1 ? 'a atividade certa' : `${results.length} atividades`} pra você` : 'Explore os passeios abaixo'}
            </h3>
            <div className="space-y-4">
              {results.map((it, i) => (
                <div key={it.id} className={`flex gap-4 p-4 rounded-xl border ${i === 0 ? 'border-coral/40 bg-coral/5' : 'border-sand-200'}`}>
                  <span className="text-4xl flex-shrink-0" aria-hidden="true">{it.icon}</span>
                  <div className="flex-1 min-w-0">
                    {i === 0 && <span className="font-body text-[11px] font-bold text-coral uppercase tracking-wide">★ Melhor combinação</span>}
                    <h4 className="font-display text-lg text-warm-dark leading-tight">{it.titulo}</h4>
                    <div className="flex flex-wrap gap-1.5 my-1.5">
                      {reasons(it, answers).map((r, j) => (
                        <span key={j} className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">{r}</span>
                      ))}
                    </div>
                    <p className="font-body text-sm text-warm-gray leading-snug mb-1">{it.blurb}</p>
                    {it.link ? (
                      <Link to={it.link} className="font-body text-sm font-semibold text-teal-deep hover:underline">Ver detalhes →</Link>
                    ) : (
                      <a href={hrefFor(it)} className="font-body text-sm font-semibold text-teal-deep hover:underline">Ver na lista de passeios ↓</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button onClick={reset} className="mt-6 font-body text-sm text-warm-gray hover:text-warm-dark">↻ Refazer o teste</button>
          </div>
        )}
      </div>
    </section>
  );
}
